export interface ElvaFederationHostBridgeOptions {
  getAccessToken: () => string | null;
  /** Host account API base URL from env, e.g. `https://dev-account-az.elva.ai/api/v2`. */
  getAccountApiBaseUrl: () => string;
  /** Additional legacy base URLs baked into a remote bundle. */
  extraLegacyAccountApiBases?: string[];
}

/** Hardcoded in the production chat remote until it reads host config. */
const DEFAULT_LEGACY_ACCOUNT_API_BASES = ['https://account-api.elva.ai/api/v1'];

function normalizeBase(url: string): string {
  return url.replace(/\/$/, '');
}

function accountApiOrigin(baseUrl: string): string {
  try {
    return new URL(baseUrl).origin;
  } catch {
    return baseUrl.replace(/\/api\/v\d+\/?$/, '');
  }
}

/**
 * chat.elva.ai still calls `GET /api/v1/users/me` on prod account-api.
 * Rewrite to the host's v2 base + `/me` so local dev tokens work.
 */
function rewriteAccountApiUrl(
  url: string,
  getAccountApiBaseUrl: () => string,
  extraLegacyBases: string[] = []
): string {
  const targetBase = normalizeBase(getAccountApiBaseUrl());
  const legacyBases = [...DEFAULT_LEGACY_ACCOUNT_API_BASES, ...extraLegacyBases.map(normalizeBase)];

  let rewritten = url;
  for (const legacy of legacyBases) {
    if (rewritten.includes(legacy)) {
      rewritten = rewritten.replace(legacy, targetBase);
      break;
    }
  }

  return rewritten.replace(/\/users\/me(?=($|[?#]))/, '/me');
}

function isAccountApiRequest(url: string, getAccountApiBaseUrl: () => string): boolean {
  const base = normalizeBase(getAccountApiBaseUrl());
  if (url.includes(base)) return true;

  const origin = accountApiOrigin(base);
  if (url.includes(origin)) return true;

  return DEFAULT_LEGACY_ACCOUNT_API_BASES.some((legacy) => url.includes(legacy));
}

function applyAccountAuthHeader(
  headers: HeadersInit | undefined,
  url: string,
  getAccessToken: () => string | null,
  getAccountApiBaseUrl: () => string
): HeadersInit | undefined {
  if (!isAccountApiRequest(url, getAccountApiBaseUrl)) {
    return headers;
  }

  const token = getAccessToken();
  if (!token) return headers;

  const next = new Headers(headers);
  if (!next.has('Authorization')) {
    next.set('Authorization', `Bearer ${token}`);
  }
  return next;
}

let bridgeInstalled = false;

/**
 * Patches XHR + fetch so federated chat remotes use the host account API (env-driven v2)
 * instead of the legacy v1 URL baked into chat.elva.ai.
 */
export function ensureElvaFederationHostBridge(options: ElvaFederationHostBridgeOptions) {
  if (bridgeInstalled || typeof window === 'undefined') {
    return;
  }

  bridgeInstalled = true;

  const { getAccessToken, getAccountApiBaseUrl, extraLegacyAccountApiBases = [] } = options;

  const rewrite = (url: string) =>
    rewriteAccountApiUrl(url, getAccountApiBaseUrl, extraLegacyAccountApiBases);

  /* eslint-disable @typescript-eslint/unbound-method -- XHR prototype patch */
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalFetch = window.fetch.bind(window);
  /* eslint-enable @typescript-eslint/unbound-method */

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    isAsync: boolean = true,
    username?: string | null,
    password?: string | null
  ) {
    const resolvedUrl = typeof url === 'string' ? rewrite(url) : rewrite(url.toString());

    (this as XMLHttpRequest & { __elvaUrl?: string }).__elvaUrl = resolvedUrl;

    originalOpen.call(this, method, resolvedUrl, isAsync, username, password);
  };

  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    const url = (this as XMLHttpRequest & { __elvaUrl?: string }).__elvaUrl;
    if (url && isAccountApiRequest(url, getAccountApiBaseUrl)) {
      const token = getAccessToken();
      if (token) {
        try {
          xhrSetAuth(this, token);
        } catch {
          // setRequestHeader can throw if called after send in edge cases
        }
      }
    }

    originalSend.call(this, body);
  };

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    const rewritten = rewrite(url);
    const isAccount = isAccountApiRequest(rewritten, getAccountApiBaseUrl);

    const nextInit: RequestInit = { ...init };
    if (isAccount) {
      const headers = applyAccountAuthHeader(
        init?.headers,
        rewritten,
        getAccessToken,
        getAccountApiBaseUrl
      );
      if (headers !== undefined) {
        nextInit.headers = headers;
      }
    }

    if (rewritten === url) {
      return originalFetch(input, nextInit);
    }

    if (typeof input === 'string') {
      return originalFetch(rewritten, nextInit);
    }
    if (input instanceof URL) {
      return originalFetch(new URL(rewritten), nextInit);
    }
    return originalFetch(new Request(rewritten, input), nextInit);
  };

  if (import.meta.env.DEV) {
    try {
      console.info('[Elva AI Federation] Host bridge installed', {
        accountApiBaseUrl: getAccountApiBaseUrl(),
        legacyBases: DEFAULT_LEGACY_ACCOUNT_API_BASES,
      });
    } catch {
      console.info('[Elva AI Federation] Host bridge installed');
    }
  }
}

function xhrSetAuth(xhr: XMLHttpRequest, token: string): void {
  xhr.setRequestHeader('Authorization', `Bearer ${token}`);
}
